// Iframe live demo — interactive playground where prospects type an
// appellation, see autocomplete suggestions, and watch the geoVINUM iframe
// reload in real time.
//
// Autocomplete is now live against the EtOH `/search/geo/autocomplete/app/`
// endpoint (proxied via our backend) — the static list is used only as a
// pre-mount cache for instant first-paint suggestions.
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Sparkles, RefreshCw } from "lucide-react";
import GeoVinumMap from "@/components/GeoVinumMap";
import { APPELLATIONS } from "@/lib/appellations";
import { useT } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const POPULAR = ["Sancerre", "Châteauneuf-du-Pape", "Chablis", "Saint-Émilion", "Barolo"];

// Diacritic-insensitive substring match — keeps "cote rotie" matching
// "Côte-Rôtie" without forcing users to find the accent keys.
const norm = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const IframeLiveDemo = () => {
  const t = useT();
  const [query, setQuery] = useState("Sancerre");
  const [appellation, setAppellation] = useState("Sancerre");
  // Stable key resets the GeoVinumMap (and the underlying iframe) whenever
  // the user picks a new appellation, ensuring a fresh token-bound URL.
  const [bumpKey, setBumpKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  const suggestions = useMemo(() => {
    const q = norm(query);
    if (!q) return APPELLATIONS.slice(0, 8);
    // Static-list fallback while the EtOH call resolves, then `liveResults`
    // (below, via useEffect) takes over once it returns.
    return APPELLATIONS.filter((a) => norm(a).includes(q)).slice(0, 8);
  }, [query]);

  const [liveResults, setLiveResults] = useState(null);
  useEffect(() => {
    const q = query.trim();
    if (!q) { setLiveResults(null); return undefined; }
    let cancelled = false;
    // 200 ms debounce — feels instant but doesn't hammer the proxy on
    // every keystroke.
    const handle = setTimeout(async () => {
      const { ok, data: d } = await apiFetch(`/api/geovinum/autocomplete?q=${encodeURIComponent(q)}`);
      if (cancelled || !ok) return;
      const names = [...new Set((d.results || []).map((it) => it.name).filter(Boolean))].slice(0, 8);
      // Only override the static list if we got real results — empty
      // EtOH response shouldn't blank the dropdown.
      if (names.length > 0) setLiveResults(names);
    }, 200);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query]);

  const displayedSuggestions = liveResults || suggestions;

  // Click-outside closes the dropdown.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const apply = (value) => {
    const v = (value || "").trim();
    if (!v) return;
    setQuery(v);
    setAppellation(v);
    setBumpKey((k) => k + 1);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, displayedSuggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); apply(suggestions[activeIdx] || query); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="iframe-live-demo">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-[5px] text-[10.5px] uppercase tracking-[0.15em] font-medium mb-4 shadow-sm">
            <Sparkles size={11} /> {t("iframeLive.eyebrow")}
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] leading-[1.1] mb-3">
            {t("iframeLive.title")}
          </h2>
          <p className="text-[14.5px] md:text-[16px] text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto">
            {t("iframeLive.subtitle")}
          </p>
        </div>

        {/* Search bar */}
        <div ref={wrapRef} className="relative max-w-xl mx-auto mb-3">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={t("iframeLive.placeholder")}
              className="w-full h-12 pl-11 pr-32 rounded-[5px] border border-[color:var(--gv-line)] bg-white text-[14.5px] focus:outline-none focus:border-[color:var(--gv-pink)] transition-colors shadow-sm"
              aria-label={t("iframeLive.cta")}
              autoComplete="off"
              data-testid="iframe-demo-search"
            />
            <button
              onClick={() => apply(query)}
              className="absolute right-1.5 top-1.5 h-9 px-4 rounded-[5px] bg-[color:var(--gv-ink)] text-white text-[13px] hover:bg-[color:var(--gv-pink)] transition-colors inline-flex items-center gap-1.5"
              data-testid="iframe-demo-apply"
            >
              <RefreshCw size={12} /> {t("iframeLive.cta")}
            </button>
          </div>

          {open && displayedSuggestions.length > 0 && (
            <ul
              className="absolute z-20 left-0 right-0 top-[calc(100%+6px)] bg-white border border-[color:var(--gv-line)] rounded-[10px] shadow-lg overflow-hidden max-h-80 overflow-y-auto"
              role="listbox"
              data-testid="iframe-demo-suggestions"
            >
              {displayedSuggestions.map((s, i) => (
                <li key={s}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => apply(s)}
                    className={`w-full text-left px-4 py-2.5 text-[13.5px] transition-colors ${
                      i === activeIdx ? "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]" : "text-[color:var(--gv-ink)]/80 hover:bg-[color:var(--gv-mist)]/50"
                    }`}
                    data-testid={`iframe-demo-suggest-${i}`}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Quick picks */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          <span className="text-[12px] text-[color:var(--gv-ink)]/50 mr-1">{t("iframeLive.suggestions_label")}</span>
          {POPULAR.map((p) => (
            <button
              key={p}
              onClick={() => apply(p)}
              className={`px-3 py-1 rounded-[5px] text-[12.5px] border transition-colors ${
                appellation === p
                  ? "bg-[color:var(--gv-pink)]/10 border-[color:var(--gv-pink)]/40 text-[color:var(--gv-pink)]"
                  : "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:border-[color:var(--gv-pink)]/50 hover:text-[color:var(--gv-pink)]"
              }`}
              data-testid={`iframe-demo-pill-${p}`}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Live iframe preview */}
        <div className="rounded-[12px] overflow-hidden border border-[color:var(--gv-line)] bg-[color:var(--gv-mist)] shadow-lg" data-testid="iframe-demo-preview-wrap">
          {/* Browser-style chrome to suggest "this is what it looks like in production". */}
          <div className="flex items-center gap-2 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)] px-4 py-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            <div className="flex-1 mx-3 h-7 bg-white border border-[color:var(--gv-line)] rounded-md px-3 flex items-center text-[11.5px] text-[color:var(--gv-ink)]/55 font-mono truncate">
              iframe.geovinum.app/?app={appellation}
            </div>
          </div>
          <GeoVinumMap key={bumpKey} appellation={appellation} />
        </div>

        <p className="text-center text-[12px] text-[color:var(--gv-ink)]/50 mt-4">
          {t("iframeLive.footer")}
        </p>
      </div>
    </section>
  );
};

export default IframeLiveDemo;
